import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { Field, Input, Label, Textarea, Button } from '@headlessui/react';
import { ToastContainer, toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import AimdocLogo from '../logo_symbol.png';
import useApi from '../api';

interface ChatInputProps {
    sendMessage: (message: string, type?: string) => void;
    config: any;
    gettingFullResponse: boolean;
    setEmail: (email: string) => void;
    setCompany: (company: string) => void;
    setFirstName: (firstName: string) => void;
    setLastName: (lastName: string) => void;
    setAttributes: Dispatch<SetStateAction<Record<string, string>>>;
    setFormSubmitted: (submitted: boolean) => void;
    leadId: string | undefined;
    fullScreen: boolean;
    socketRef?: React.RefObject<WebSocket>;
    conversationId?: string;
    hideInput?: boolean;
    formFields?: Array<{
        name: string;
        value: string;
        label: string;
        type: string;
    }>;
    trackWidgetClick?: any;
    isMobile?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
    sendMessage,
    config,
    gettingFullResponse,
    setEmail,
    setCompany,
    setFirstName,
    setLastName,
    setAttributes,
    setFormSubmitted,
    leadId,
    fullScreen,
    socketRef,
    conversationId,
    hideInput,
    formFields = [],
    trackWidgetClick,
    isMobile
}) => {
    const [input, setInput] = useState('');
    const [leadFirstName, setLeadFirstName] = useState('');
    const [leadLastName, setLeadLastName] = useState('');
    const [leadCompany, setLeadCompany] = useState('');
    const [leadEmail, setLeadEmail] = useState('');
    const [leadJobTitle, setLeadJobTitle] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [formData, setFormData] = useState<Record<string, string>>({});
    const leadEmailRef = useRef('');
    leadEmailRef.current = leadEmail;
    const leadFirstNameRef = useRef('');
    leadFirstNameRef.current = leadFirstName;
    const leadLastNameRef = useRef('');
    leadLastNameRef.current = leadLastName;
    const leadCompanyRef = useRef('');
    leadCompanyRef.current = leadCompany;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { post } = useApi();

    function debounce<T extends (...args: any[]) => any>(func: T, delay: number) {
        let timeoutId: NodeJS.Timeout | null = null;

        const debounced = function (this: any, ...args: Parameters<T>) {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };

        debounced.cancel = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = null;
        };

        return debounced as typeof debounced & { cancel: () => void };
    }

    const debouncedSendTypingStatus = useRef(
        debounce((typing: boolean) => sendTypingStatus(typing), 300)
    ).current;

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setInput(newValue);

        if (newValue && !isTyping) {
            setIsTyping(true);
            sendTypingStatus(true);
        } else if (!newValue && isTyping) {
            setIsTyping(false);
            sendTypingStatus(false);
        } else {
            debouncedSendTypingStatus(!!newValue);
        }
    };

    useEffect(() => {
        return () => {
            if (isTyping) {
                sendTypingStatus(false);
            }
            debouncedSendTypingStatus.cancel();
        };
    }, [isTyping, debouncedSendTypingStatus]);

    const sendTypingStatus = (typing: boolean) => {
        if (socketRef && socketRef.current && conversationId) {
            socketRef.current.send(JSON.stringify({
                type: 'visitor_typing',
                is_typing: typing,
                conversation_id: conversationId
            }));
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (input === '' || gettingFullResponse) {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage(input, 'user');
            setIsTyping(false);
            setInput('');
        }
    };

    const handleClick = () => {
        if (input === '' || gettingFullResponse) {
            return;
        }
        sendMessage(input, 'user');
        setIsTyping(false);
        setInput('');
    };

    const adjustTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    useEffect(() => {
        adjustTextareaHeight();
    }, [input]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, []);

    const validateAndSubmitForm = async () => {
        const requiredFields = config.form
            .filter((field: any) => field.required && !field.hidden);
        const missingFields = requiredFields.filter((field: any) => !formData[field.name]);

        if (missingFields.length > 0) {
            toast.error(`Please fill in the following required fields: ${missingFields.map((f: any) => f.label).join(', ')}`, toastConfig);
            return;
        }

        const emailField = config.form.find((field: any) => field.type === 'email' && !field.hidden);
        if (emailField && formData[emailField.name]) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData[emailField.name])) {
                toast.error('Please enter a valid email address', toastConfig);
                return;
            }

            if (config.work_email_only) {
                try {
                    const response = await post('/verify-email/', { email: formData[emailField.name] });
                    if (!response.data) {
                        toast.error('Please enter a work email address', toastConfig);
                        return;
                    }
                } catch (error) {
                    toast.error('Error verifying email address', toastConfig);
                    return;
                }
            }
        }

        config.form.forEach((field: any) => {
            switch (field.name) {
                case 'email':
                    setEmail(formData[field.name] || '');
                    break;
                case 'company':
                    setCompany(formData[field.name] || '');
                    break;
                case 'first_name':
                    setFirstName(formData[field.name] || '');
                    break;
                case 'last_name':
                    setLastName(formData[field.name] || '');
                    break;
                default:
                    setAttributes((prevAttributes: Record<string, string>) => ({
                        ...prevAttributes,
                        [field.name]: (formData[field.name] as string) || ''
                    }));
                    break;
            }
        });

        setFormSubmitted(true);
        setShowForm(false);
    };

    const handleFieldChange = (fieldName: string, value: string) => {
        setFormData(prevData => ({ ...prevData, [fieldName]: value }));
    };

    useEffect(() => {
        if (formFields.length > 0) {
            const newFormData = formFields.reduce((acc, field) => ({
                ...acc,
                [field.name]: field.value || ''
            }), {});

            setFormData(prevData => ({
                ...prevData,
                ...newFormData
            }));
        }
    }, [formFields]);

    const handleFormButtonClick = () => {
        trackWidgetClick('BUTTON', 'Open Form', 'form_open');
        setShowForm(true);
    };

    console.log(isMobile);

    return (
        <div className={`sticky bottom-0 z-30 w-full`}>
            <ToastContainer {...toastConfig} />
            {!leadId && !fullScreen && (
                <div className="flex flex-row items-center pr-1 w-full bg-white justify-center">
                    <div className="flex flex-col w-full max-w-6xl">
                        <div className={`overflow-hidden transition-max-height duration-1000 ease-in-out ${showForm ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                            {showForm && (
                                <div className="px-6 py-3">
                                    <div className="mb-3">
                                        <h3 className="text-sm font-semibold text-slate-800" style={{ color: config.color }}>Contact Information</h3>
                                        <p className="text-xs text-slate-500 mt-1">Please provide your details and we'll get back to you shortly.</p>
                                    </div>
                                    <div className="space-y-3">
                                        {config.form
                                            .filter((field: any) => !field.hidden)
                                            .map((field: any) => (
                                                <Field key={field.name} className="mb-1">
                                                    <Label className={`${isMobile ? 'text-sm' : 'text-xs'} font-medium text-slate-700 mb-1 block`}>
                                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                                    </Label>
                                                    {field.type === 'dropdown' ? (
                                                        <select
                                                            className={`block w-full rounded-md border border-slate-200 py-2 px-3 ${isMobile ? 'text-base' : 'text-sm'} text-slate-900 focus:ring-2 focus:ring-opacity-50 focus:outline-none bg-white shadow-sm`}
                                                            style={{ 
                                                                borderColor: formData[field.name] ? `${config.color}30` : ''
                                                            }}
                                                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                            required={field.required}
                                                            value={formData[field.name] || ''}
                                                        >
                                                            <option value=""></option>
                                                            {field.options?.map((option: string) => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <Input
                                                            type={field.type}
                                                            className={`block w-full rounded-md border border-slate-200 py-2 px-3 ${isMobile ? 'text-base' : 'text-sm'} text-slate-900 focus:ring-2 focus:ring-opacity-50 focus:outline-none shadow-sm`}
                                                            style={{ 
                                                                borderColor: formData[field.name] ? `${config.color}30` : ''
                                                            }}
                                                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                            required={field.required}
                                                            value={formData[field.name] || ''}
                                                            placeholder={`Enter your ${field.label.toLowerCase()}`}
                                                        />
                                                    )}
                                                </Field>
                                            ))}
                                    </div>
                                    <div className="flex items-center justify-between mt-4">
                                        <button 
                                            onClick={() => setShowForm(false)} 
                                            className={`${isMobile ? 'text-sm' : 'text-xs'} text-slate-500 hover:text-slate-700 transition-colors`}
                                        >
                                            Cancel
                                        </button>
                                        <Button
                                            onClick={async () => await validateAndSubmitForm()}
                                            className={`shrink-0 items-center rounded-md border py-2 px-4 ${isMobile ? 'text-sm' : 'text-xs'} font-medium focus:outline-none transition duration-200`}
                                            style={{ 
                                                backgroundColor: config.color,
                                                color: 'white',
                                                borderColor: `${config.color}80`
                                            }}
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {!hideInput && (
                <>
                    <div className="mx-auto flex flex-col max-w-6xl pl-4 pr-4">
                        <div className="w-full rounded-t-2xl bg-white border border-slate-200 border-b-0">
                            <Textarea
                                ref={textareaRef}
                                className={`w-full bg-white/90 mt-2 py-1 px-3 border-none ${isMobile ? 'text-base placeholder:text-base' : 'text-sm placeholder:text-sm'} text-slate-900 focus:outline-none focus:ring-0 rounded-lg focus:ring-blue-200 focus:border-transparent resize-none overflow-hidden`}
                                id="message"
                                placeholder={config?.translations?.input_placeholder}
                                autoComplete="off"
                                value={input}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                rows={1}
                            />
                        </div>
                        <div className="flex items-center justify-between w-full rounded-b-2xl bg-white border border-slate-200 border-t-0 p-2">
                            {!leadId ? (
                                <button
                                    onClick={handleFormButtonClick}
                                    style={{
                                        backgroundColor: `${config.color}10`,
                                        borderColor: `${config.color}20`,
                                        color: `${config.color}90`,
                                    }}
                                    className={`flex items-center justify-center ${isMobile ? 'text-sm' : 'text-xs'} font-medium rounded-xl hover:cursor-pointer hover:scale-105 transition duration-200 px-2 py-1 border ml-1`}
                                >
                                    Contact Us
                                </button>
                            ) : (
                                <div className="flex-grow"></div>
                            )}
                            <button
                                onClick={handleClick}
                                disabled={gettingFullResponse}
                                style={{
                                    backgroundColor: `${config.color}`,
                                    opacity: gettingFullResponse || input === '' ? 0.2 : 0.7
                                }}
                                className={`flex items-center justify-center text-white rounded-xl hover:cursor-pointer hover:scale-105 transition duration-200 px-3 py-1 mr-1`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`${isMobile ? 'size-5' : 'size-4'}`}>
                                    <path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.155.75.75 0 0 0 0-1.114A28.897 28.897 0 0 0 3.105 2.288Z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="flex justify-center mt-3 pl-6 pr-6 bg-slate-50 py-1 border-t border-slate-100">
                        <a target="_blank" href="https://aimdoc.ai" className="text-[9px] text-slate-700 font-normal hover:scale-110 transform duration-200 flex items-center gap-1" rel="noreferrer">
                            {config.hide_aimdoc ? '' : (
                                <>
                                    {/* {config.type === 'product' ? 'product' : 'website'} */}
                                    <span>Powered by <strong>Aimdoc</strong></span><img src={AimdocLogo} alt="Aimdoc Logo" className="w-3 h-3 grayscale" />
                                </>
                            )}
                        </a>
                    </div>
                </>
            )}
        </div>
    );
};

export default ChatInput;
